import React from "react";

/**
 * AppErrorBoundary — last-resort safety net so a JS exception in a single
 * page (gallery render bug, malformed cache, missing import, etc.) doesn't
 * leave the user staring at a blank cream screen on TestFlight with no way
 * to recover except force-closing the app.
 *
 * - Catches errors thrown during render or in lifecycle methods of any
 *   descendant. Hooks-related errors are also caught.
 * - Renders a quiet, on-brand recovery UI with a "Reload" button that
 *   does `window.location.reload()` and a "Sign out" escape hatch.
 * - In dev/preview, also surfaces the error message so the next iteration
 *   can debug it without having to reproduce manually.
 *
 * Keep this LOW in the tree (per-route) AND at the App root so a single
 * page-level crash doesn't take the whole app down with it.
 */
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[Kinly] Boundary caught:", error, info?.componentStack);
  }
  reload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };
  signOut = () => {
    try {
      // Nuke ALL kinly.* keys (auth tokens, cached lists, prefs) so a
      // corrupted state can't rehydrate the page back into the crash.
      const toDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("kinly.")) toDelete.push(k);
      }
      toDelete.forEach((k) => localStorage.removeItem(k));
    } catch { /* noop */ }
    try { window.location.assign("/signin"); } catch { /* noop */ }
  };
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid="error-boundary"
        className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center bg-background"
        role="alert"
      >
        <div className="w-12 h-12 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-4 font-display text-xl">!</div>
        <h2 className="font-display text-[20px] font-medium mb-2">Kinly hit a snag.</h2>
        <p className="text-[14px] text-muted-foreground mb-6 max-w-xs leading-relaxed">
          This screen failed to load. We've logged the details. A reload usually puts it right.
        </p>
        <div className="flex gap-2">
          <button
            data-testid="error-boundary-reload"
            onClick={this.reload}
            className="h-11 px-5 rounded-full bg-foreground text-background text-sm font-medium tap"
          >
            Reload
          </button>
          <button
            data-testid="error-boundary-signout"
            onClick={this.signOut}
            className="h-11 px-5 rounded-full border border-border text-foreground text-sm font-medium tap"
          >
            Sign out
          </button>
        </div>
        {process.env.NODE_ENV !== "production" && (
          <pre className="mt-6 text-[10px] text-destructive whitespace-pre-wrap max-w-full overflow-x-auto opacity-70">
            {String(this.state.error?.message || this.state.error)}
          </pre>
        )}
      </div>
    );
  }
}
